import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { Button2 } from "@/components/UI2/atoms/Button";
import CloseIcon from "@/assets/images/icons2/icon--close.svg?component";
import EditIcon from "@/assets/images/icons2/icon--edit.svg?component";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import type { NoteDetail, UUID } from "@/core/types";
import PrintIcon from "@/assets/images/icons2/icon--print.svg?component";
import { testLocator } from "@/lib/helpers/test-locator";
import TrashIcon from "@/assets/images/icons2/icon--trash.svg?component";

type Props = {
  isEditable: boolean;
  isEditing: boolean;
  note: NoteDetail;
  patientId: string;
  setDraftNoteId: (id: UUID) => void;
  setIsEditing: (value: boolean) => void;
  setShowDeleteNotePopUp: (value: boolean) => void;
};

export function NoteActions({
  isEditable,
  isEditing,
  note,
  patientId,
  setDraftNoteId,
  setIsEditing,
  setShowDeleteNotePopUp,
}: Props) {
  const history = useHistory();

  const handleEditPress = useCallback(() => {
    // Care Management and Advanced Care Activity both edit in-place via
    // NoteContentRouter's isEditing prop instead of routing to the legacy
    // DraftNoteView, which has no awareness of either note type's shape.
    if (note.note_type === "Care Management" || note.note_type === "Advanced Care Activity") {
      return setIsEditing(true);
    }

    history.push(
      `/app/patient/${patientId}/note/${note.id}/${note.version === 2 ? "v2/" : ""}draft`,
    );
  }, [note, patientId, setIsEditing, history]);

  return (
    <div className="tw:flex tw:justify-end tw:gap-4 tw:print:hidden">
      {isEditable && !isEditing && (
        <>
          <Button2
            className="tw:flex tw:items-center tw:gap-1 tw:text-text-bright-purple-primary"
            onPress={handleEditPress}
            rawChildren
            variant="blank"
            {...getAutomationDataAttr("note-actions-edit-button")}
            {...testLocator("noteEditBtn")}
          >
            <EditIcon className="tw:size-6" />
            <span className="tw:text-base">Edit</span>
          </Button2>

          <Button2
            className="tw:flex tw:items-center tw:gap-1 tw:text-text-bright-purple-primary"
            onPress={() => {
              history.push(`/app/patient/${patientId}/export_notes?noteIds=${note.id}`);
            }}
            rawChildren
            variant="blank"
          >
            <PrintIcon className="tw:size-6" />
            <span className="tw:text-base">Print</span>
          </Button2>

          <Button2
            className="tw:flex tw:items-center tw:gap-1 tw:text-text-error-primary"
            isDisabled={note.note_state === "rejected"}
            onPress={() => {
              setShowDeleteNotePopUp(true);
              setDraftNoteId(note.id);
            }}
            rawChildren
            variant="blank"
            {...testLocator("noteDeleteBtn")}
          >
            <TrashIcon className="tw:size-6" />
            <span className="tw:text-base">Delete Draft</span>
          </Button2>
        </>
      )}

      {isEditing && (
        <Button2
          className="tw:flex tw:items-center tw:gap-1 tw:text-text-bright-purple-primary"
          onPress={() => setIsEditing(false)}
          rawChildren
          variant="blank"
        >
          <CloseIcon className="tw:size-4" />
          <span className="tw:text-base">Discard Changes</span>
        </Button2>
      )}
    </div>
  );
}
